/**
 * An icon per kind of engineering file.
 *
 * Icons are chosen to say what the file IS to an engineer, not what format it
 * happens to be: a part, an assembly, a dimensioned sheet, a released
 * document, a neutral exchange model. A folder of eighty files should be
 * scannable without reading a single extension.
 */
import {
  Folder, FolderOpen, Box, Boxes, PencilRuler, FileText, Shapes,
  Waves, NotebookPen, FileCode, Image, FileArchive, File,
} from 'lucide-react'
import { kindOf, type FileKind, type Path } from '@lattice/core'
import { cn } from '@/lib/utils'

const ICONS: Record<FileKind, typeof File> = {
  part: Box,
  assembly: Boxes,
  drawing: PencilRuler,
  document: FileText,
  exchange: Shapes,
  simulation: Waves,
  notebook: NotebookPen,
  text: FileCode,
  image: Image,
  archive: FileArchive,
  other: File,
}

/**
 * Kinds that carry geometry are tinted; everything else stays neutral.
 * A little colour where it means something reads as careful; colour everywhere
 * reads as decoration.
 */
const TINTS: Partial<Record<FileKind, string>> = {
  part: 'text-[var(--accent)]',
  assembly: 'text-[var(--renamed)]',
  drawing: 'text-[var(--modified)]',
  exchange: 'text-[var(--added)]',
}

export function FileIcon({ path, className }: { path: string; className?: string }) {
  const kind = kindOf(path as Path)
  const Icon = ICONS[kind]
  return <Icon className={cn('h-3.5 w-3.5 shrink-0', TINTS[kind] ?? 'text-[var(--fg-subtle)]', className)} strokeWidth={1.75} />
}

export function FolderIcon({ open, className }: { open?: boolean; className?: string }) {
  const Icon = open ? FolderOpen : Folder
  return <Icon className={cn('h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]', className)} strokeWidth={1.75} />
}
